import React, { useCallback, useEffect } from "react";
import { message } from "antd";
import LeoChatWidget from "../leo/LeoChatWidget";
import { trackEvent } from "../../helpers/mixpanel";
import { useMobileWidgetContext } from "../../context/MobileWidgetContext";
import AvailabilityTimePickerCard from "./chat/AvailabilityTimePickerCard";
import TourConfirmedCard from "./chat/TourConfirmedCard";
import {
  populateChatWithSimulation,
  DEBUG_FLOWS,
} from "../../helpers/listingChatDebug";

/**
 * PublicLeoChat - Public-facing Leo chat widget for vacancy landing pages
 *
 * This component wraps LeoChatWidget with public listing-specific API calls and configuration.
 *
 * API Endpoints:
 * - GET /api/public/listing-chat/initial-message/:listingId
 * - POST /api/public/listing-chat/send-message
 *
 * @param {Object} props
 * @param {Object} props.listingData - The listing data object
 * @param {string} props.listingId - The listing ID (from URL params)
 * @param {boolean} props.isVisible - Whether the widget should be visible
 */
const PublicLeoChat = ({
  listingData,
  listingId,
  isVisible = true,
  openSignal,
}) => {
  // Debug mode: Check for ?debug=flowType URL parameter
  // Populate debug messages synchronously BEFORE widget initializes
  if (listingId && typeof window !== "undefined") {
    const urlParams = new URLSearchParams(window.location.search);
    const debugFlow = urlParams.get("debug");

    if (debugFlow && DEBUG_FLOWS[debugFlow]) {
      console.log(
        `[PublicLeoChat] Debug mode: Populating chat with ${debugFlow} flow`
      );
      populateChatWithSimulation(listingId, debugFlow);
    }
  }

  // Storage key helpers
  const getChatStorageKey = useCallback(() => {
    return `public-leo-chat-${listingId || "anonymous"}`;
  }, [listingId]);

  const getChatTimestampKey = useCallback(() => {
    return `public-leo-chat-timestamp-${listingId || "anonymous"}`;
  }, [listingId]);

  // Fetch initial message from public listing API
  const fetchInitialMessage = useCallback(async () => {
    if (!listingId) {
      return {
        id: Date.now(),
        type: "ai",
        content: `Hi! I'm Leo, your AI assistant. I can help you with general questions.`,
        timestamp: new Date(),
      };
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        throw new Error("REACT_APP_API_URL not configured");
      }

      // Determine the correct endpoint path
      const endpointPath = apiUrl.endsWith("/api")
        ? `/public/listing-chat/initial-message/${listingId}`
        : `/api/public/listing-chat/initial-message/${listingId}`;
      const fullUrl = `${apiUrl}${endpointPath}`;

      console.log("[PublicLeoChat] Fetching initial message from:", fullUrl);

      // Prevent infinite loading if the backend hangs
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 12000);

      const response = await fetch(fullUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.text) {
          return {
            id: Date.now(),
            type: "ai",
            content: data.text,
            timestamp: new Date(),
          };
        }
      }

      // Return a default message if API fails
      return {
        id: Date.now(),
        type: "ai",
        content:
          "Hi! I'm Leo, your AI assistant for this property. What would you like to know?",
        timestamp: new Date(),
      };
    } catch (error) {
      console.error("Error fetching initial message:", error);
      // Return a default message on error
      return {
        id: Date.now(),
        type: "ai",
        content:
          "Hi! I'm Leo, your AI assistant for this property. What would you like to know?",
        timestamp: new Date(),
      };
    }
  }, [listingId]);

  // Get sessionId from localStorage
  const getSessionId = useCallback(() => {
    if (!listingId) return null;
    return localStorage.getItem(`listing_chat_session_${listingId}`);
  }, [listingId]);

  // Save sessionId to localStorage
  const saveSessionId = useCallback(
    (sessionId) => {
      if (!listingId || !sessionId) return;
      localStorage.setItem(`listing_chat_session_${listingId}`, sessionId);
    },
    [listingId]
  );

  // Handle sending message via public listing API
  const handleSendMessage = useCallback(
    async (messageText, currentMessages) => {
      if (!listingId) {
        console.error("[PublicLeoChat] No listingId provided");
        throw new Error("Listing ID is required");
      }

      if (typeof listingId !== "string" || listingId.trim().length === 0) {
        console.error("[PublicLeoChat] Invalid listingId:", listingId);
        throw new Error("Invalid listing ID");
      }

      console.log(
        "[PublicLeoChat] Sending message with listingId:",
        listingId,
        "Type:",
        typeof listingId
      );

      // Track message sent
      trackEvent("public_listing_leo_message_sent", {
        listing_id: listingId,
        message_length: messageText.length,
        message_count: currentMessages.length + 1,
        is_first_message: currentMessages.length === 0,
      });

      // Prepare chat history
      const chatHistory = currentMessages.map((msg) => ({
        from: msg.type === "user" ? "user" : "assistant",
        text: msg.content,
        timestamp: msg.timestamp.toISOString(),
        images: msg.images || [],
      }));

      chatHistory.push({
        from: "user",
        text: messageText,
        timestamp: new Date().toISOString(),
        images: [],
      });

      // Get sessionId from localStorage (or omit for first message)
      const sessionId = getSessionId();

      const buildApiUrl = (path) => {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) throw new Error("API URL not configured");
        const endpointPath = apiUrl.endsWith("/api")
          ? path.replace(/^\/api/, "")
          : path;
        return `${apiUrl}${endpointPath}`;
      };

      const fetchAvailability = async () => {
        const url = buildApiUrl(
          `/api/availability?listingId=${encodeURIComponent(listingId)}`
        );
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 12000);
        try {
          const res = await fetch(url, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            signal: controller.signal,
          });
          clearTimeout(timeoutId);
          if (!res.ok) return null;
          return await res.json();
        } catch (e) {
          clearTimeout(timeoutId);
          return null;
        }
      };

      const bookTour = async ({ slot }) => {
        const url = buildApiUrl("/api/book-tour");
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000);
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              listingId,
              slot: slot?.raw || slot,
              slotIso: slot?.iso || null,
              timezone: tz,
            }),
            signal: controller.signal,
          });
          clearTimeout(timeoutId);
          if (!res.ok) throw new Error(`Booking failed (${res.status})`);
          return await res.json();
        } catch (e) {
          clearTimeout(timeoutId);
          throw e;
        }
      };

      // Call the public sendChat API
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        throw new Error("API URL not configured");
      }

      // Determine the correct endpoint path
      // If apiUrl already includes /api, use /public/listing-chat/
      // Otherwise, use /api/public/listing-chat/
      const endpointPath = apiUrl.endsWith("/api")
        ? "/public/listing-chat/send-message"
        : "/api/public/listing-chat/send-message";
      const fullUrl = `${apiUrl}${endpointPath}`;

      console.log("[PublicLeoChat] API URL:", fullUrl);
      console.log("[PublicLeoChat] Request body:", {
        listingId,
        messageLength: messageText.length,
        hasSessionId: !!sessionId,
      });

      try {
        const requestBody = {
          listingId: listingId,
          message: messageText,
          chatHistory: chatHistory,
        };

        // Include sessionId if we have one
        if (sessionId) {
          requestBody.sessionId = sessionId;
        }

        const response = await fetch(fullUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          if (response.status === 400) {
            try {
              const errorData = await response.json();
              if (
                Array.isArray(errorData) &&
                errorData[0]?.errors?.issues?.[0]?.message ===
                  "Message cannot be empty"
              ) {
                throw new Error("Please enter a message before sending.");
              }
              const validationMessage =
                errorData[0]?.errors?.issues?.[0]?.message ||
                "Invalid input. Please check your message.";
              throw new Error(validationMessage);
            } catch (parseError) {
              console.error("Failed to parse validation error:", parseError);
            }
          }

          if (response.status === 404) {
            console.error(
              "[PublicLeoChat] 404 Error - Listing not found:",
              listingId
            );
            console.error("[PublicLeoChat] Response URL:", fullUrl);
            // Try to get error details from response
            try {
              const errorData = await response.json();
              console.error("[PublicLeoChat] Error details:", errorData);
              throw new Error(
                errorData.error || "Listing not found or no longer available."
              );
            } catch (parseError) {
              throw new Error(
                "Listing not found or no longer available. Please check the listing ID."
              );
            }
          }

          const errorText = await response.text();
          console.error(
            "[PublicLeoChat] API error:",
            response.status,
            errorText
          );
          // Try to surface the human-readable message from the backend if available
          let userFacingMessage = "Something went wrong. Please try again.";
          try {
            const errJson = JSON.parse(errorText);
            if (errJson?.error && typeof errJson.error === "string") {
              userFacingMessage = errJson.error;
            }
          } catch {
            // errorText wasn't JSON; keep the default
          }
          throw new Error(userFacingMessage);
        }

        const data = await response.json();

        // State-Adaptive: guardrail blocked message (high-risk state + medical keyword); show notice and clear input
        if (data.consent_required === true) {
          message.warning(
            "Your message may contain health-related information. We don’t collect or use health data in this chat. Please rephrase or contact the property directly for health-related questions.",
            6
          );
          return { consent_required: true, text: "" };
        }

        if (!data.success) {
          trackEvent("public_listing_leo_message_error", {
            listing_id: listingId,
            error: data.error || "Unknown error",
          });
          throw new Error(data.error || "Failed to get response from Leo.");
        }

        // Save sessionId if provided
        if (data.sessionId) {
          saveSessionId(data.sessionId);
        }

        // Track successful response with conversation state
        trackEvent("public_listing_leo_message_received", {
          listing_id: listingId,
          response_length: (data.message?.text || "").length,
          conversation_stage: data.conversationState?.stage || "unknown",
          is_qualified: data.conversationState?.isQualified,
          requires_action: data.requiresAction?.type || null,
        });

        // Handle requiresAction - show notifications
        // IMPORTANT: Only show "email sent" if the backend explicitly confirms it.
        // `requiresAction.type === 'send_background_check'` can mean "needs to be sent", not "already sent".
        if (data.requiresAction?.type === "send_background_check") {
          const ra = data.requiresAction || {};
          const emailSent =
            ra.emailSent === true ||
            ra.sent === true ||
            ra.completed === true ||
            ra.status === "sent" ||
            ra.result === "sent";

          if (emailSent) {
            message.success(
              "Background check email sent! Please check your inbox."
            );
          }
        }

        // Return response with conversation state
        const result = {
          text:
            data.message?.text ||
            data.message ||
            "Sorry, I encountered an error. Please try again.",
          conversationState: data.conversationState || null,
          requiresAction: data.requiresAction || null,
        };

        // Only show availability picker if backend explicitly requests it via requiresAction
        if (data.requiresAction?.type === "schedule_tour") {
          const alreadyHasPicker = (currentMessages || []).some(
            (m) => m?.kind === "availability_picker"
          );
          const alreadyBooked = (currentMessages || []).some(
            (m) => m?.kind === "tour_confirmed"
          );

          if (!alreadyHasPicker && !alreadyBooked) {
            const availability = await fetchAvailability();
            const slots =
              availability?.slots ||
              availability?.availableSlots ||
              availability?.timeslots ||
              availability?.times ||
              [];
            result.additionalMessages = [
              {
                id: Date.now() + 2,
                type: "ai",
                kind: "availability_picker",
                payload: { slots },
                content: "",
                timestamp: new Date(),
              },
            ];
          }
        }

        return result;
      } catch (fetchError) {
        clearTimeout(timeoutId);

        trackEvent("public_listing_leo_message_failed", {
          listing_id: listingId,
          error_type: fetchError.name,
          error_message: fetchError.message,
        });

        if (fetchError.name === "AbortError") {
          throw new Error("Request timed out. Please try again.");
        }
        throw fetchError;
      }
    },
    [listingId, getSessionId, saveSessionId]
  );

  // Tooltip text for public listing page - pre-screening context
  const tooltipText = "Get pre-screened, qualify, and book your tour with Leo!";

  // Track widget open/close
  const handleWidgetToggle = useCallback(
    (isOpen) => {
      trackEvent(
        isOpen
          ? "public_listing_leo_widget_opened"
          : "public_listing_leo_widget_closed",
        {
          listing_id: listingId,
        }
      );
    },
    [listingId]
  );

  // Handle chat reset - clear session ID storage for this listing
  const handleChatReset = useCallback(() => {
    if (listingId) {
      try {
        localStorage.removeItem(`listing_chat_session_${listingId}`);
      } catch (error) {
        console.warn(
          "[PublicLeoChat] Failed to clear session ID on reset:",
          error
        );
      }
    }
  }, [listingId]);

  const { isFeedbackWidgetActive, setActiveWidget, clearActiveWidget } =
    useMobileWidgetContext();

  return (
    <LeoChatWidget
      isVisible={isVisible}
      fullScreenOnDesktop={true}
      fetchInitialMessage={fetchInitialMessage}
      handleSendMessage={handleSendMessage}
      getChatStorageKey={getChatStorageKey}
      getChatTimestampKey={getChatTimestampKey}
      openSignal={openSignal}
      onReset={handleChatReset}
      renderCustomMessage={(msg, { onAction }) => {
        if (msg?.kind === "availability_picker") {
          const slots = msg?.payload?.slots || [];
          return (
            <AvailabilityTimePickerCard
              slots={slots}
              onSelect={(slot) =>
                onAction?.({ type: "select_availability_slot", slot })
              }
            />
          );
        }
        if (msg?.kind === "tour_confirmed") {
          return (
            <TourConfirmedCard
              address={msg?.payload?.address}
              whenLabel={msg?.payload?.whenLabel}
            />
          );
        }
        if (msg?.kind === "application_cta") {
          const href = msg?.payload?.url;
          if (!href) return null;
          return (
            <div style={{ lineHeight: 1.35 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                To speed things up, you can start your application now
                (optional):
              </div>
              <a href={href} target="_blank" rel="noopener noreferrer">
                Start application →
              </a>
            </div>
          );
        }
        return null;
      }}
      onCustomAction={async (action, helpers) => {
        if (action?.type !== "select_availability_slot") return;
        const slot = action?.slot;

        // Echo selection as a user message (since this wasn't typed)
        helpers.appendMessages?.([
          {
            id: Date.now(),
            type: "user",
            content: slot?.label || "Selected a time",
            timestamp: new Date(),
          },
        ]);

        helpers.setIsTyping?.(true);

        try {
        // Book via backend
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) throw new Error("API URL not configured");
        const endpointPath = apiUrl.endsWith("/api")
          ? "/book-tour"
          : "/api/book-tour";
        const fullUrl = `${apiUrl}${endpointPath}`;

        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await fetch(fullUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            listingId,
            slot: slot?.raw || slot,
            slotIso: slot?.iso || null,
            timezone: tz,
          }),
        });
        if (!res.ok) {
          helpers.setIsTyping?.(false);
          const errBody = await res.json().catch(() => ({}));
          message.error(errBody?.error || "Could not book that time. Please try another slot.");
          return;
        }
        const booking = await res.json();

        const whenLabel =
          booking?.whenLabel ||
          booking?.display ||
          booking?.slotLabel ||
          slot?.label ||
          (slot?.iso ? new Date(slot.iso).toLocaleString() : "Scheduled");

        // Confirm card
        helpers.appendMessages?.([
          {
            id: Date.now() + 1,
            type: "ai",
            kind: "tour_confirmed",
            payload: {
              address: listingData?.address || booking?.address || null,
              whenLabel,
            },
            content: "",
            timestamp: new Date(),
          },
        ]);

        // Optional application CTA (background check link)
        const url =
          booking?.backgroundCheckUrl ||
          booking?.applicationUrl ||
          booking?.link ||
          booking?.url ||
          null;
        if (url) {
          helpers.appendMessages?.([
            {
              id: Date.now() + 2,
              type: "ai",
              kind: "application_cta",
              payload: { url },
              content: "",
              timestamp: new Date(),
            },
          ]);
        }

        helpers.setIsTyping?.(false);
        message.success("Tour booked!");
        } catch (bookingErr) {
          helpers.setIsTyping?.(false);
          message.error(
            bookingErr?.message || "Something went wrong booking your tour. Please try again."
          );
        }
      }}
      tooltipText={tooltipText}
      iconPath="/icons/leo/leo.png"
      buttonIconPath="/icons/leo/leo-chat.png"
      title="Leo AI Assistant"
      subtitle="Your AI assistant for this listing"
      placeholder="Ask Leo AI about this property..."
      showWelcomeTooltip={true} // Show tooltip for pre-screening context
      requireAuth={false}
      showFullDisclaimer={true} // Show full disclaimer with terms/privacy links for public listings
      showMedicalDisclaimer={true} // Show medical/health disclaimer below input for listing chat
      onWidgetToggle={handleWidgetToggle}
      isFeedbackWidgetActive={isFeedbackWidgetActive}
      setActiveWidget={setActiveWidget}
      clearActiveWidget={clearActiveWidget}
    />
  );
};

export default PublicLeoChat;
